import React from 'react';
import ResizeObserver from 'resize-observer-polyfill'
import qs from 'query-string'

import {
  NODE_TYPE as NT,
  RENDER_MODE as RM,
} from 'shared/constants/slate';
import {
  IFRAMELY_API_KEY,
  IFRAMELY_API_ENDPOINT,
  IFRAMELY_RELAY_ENDPOINT,
} from 'shared/constants/external';
import {
  noop,
  timeoutPromiseAt,
} from 'shared/helpers';

import { cx, styled } from 'helpers';
import {
  Selectible,
  MessageFrame,
  MediaLoading,
  FetchErrored,
} from './extra';
import {
  makeNodeProps,
  handleOrphanReceiverNode,
} from '../utils';



class EmbedReceiver extends React.Component {
  render() {
    const { attributes: attrs, children } = this.props;

    return (
      <div className="relative ph2 bg-paper-white" {...attrs}>
        {children}

        <MessageFrame className="pointer-events-none">
          Paste a link to embed content from another site..
        </MessageFrame>
      </div>
    );
  }
}


const ToggleButton = ({ label, isActive, onClick }) => (
  <button
    className={cx(
      "-button-reset",
      isActive ? 'caret-blue': 'moon-gray'
    )}
    disabled={isActive ? true : false}
    onClick={onClick}
  >
    {label}
  </button>
)

const LessOrMoreToggle = ({ smaller = false, onToggle }) => (
  <div
    className="moa-ff-sans fw3 moon-gray"
    style={{ fontSize: '11px' }}
  >
    <ToggleButton label="Less" isActive={smaller} onClick={onToggle} />
    <span className="mh1">/</span>
    <ToggleButton label="More" isActive={!smaller} onClick={onToggle} />
  </div>
)

const EMBED_ACTION_MENU_CLASS = 'embed-action-menu'
const EmbedActionMenu = ({ children }) => (
  <div
    className={cx(
      EMBED_ACTION_MENU_CLASS,
      'absolute flex items-center justify-end',
      'vis-hidden'  // overriden by Figure below
    )}
    style={{
      // Make sure no gap from Feature, because hover rule.
      top: '-1.5rem', height: '1.5rem',
      left: 0, right: 0,
    }}
  >
    {children}
  </div>
)


// Visibility controlled by the combination of css pseudo
// and isSelected state
const visVisible = { visibility: 'visible' }
const Figure = styled('figure')(props => {
  const visibleOnHover = {
    [`&:hover .${EMBED_ACTION_MENU_CLASS}`]: visVisible,
  }

  const { isSelected } = props
  const visibleOnSelect = (isSelected
    ? {[`& .${EMBED_ACTION_MENU_CLASS}`]: visVisible}
    : {}
  )

  return { ...visibleOnHover, ...visibleOnSelect }
})

const HtmlFrame = styled(Selectible)({
  minHeight: '1rem',

  // Needed for img grand child when in preview mode, otherwise
  // seems to be inheriting `white-space: pre-wrap` style given
  // by Slate at editor level, which puts extra vertical spacing.
  whiteSpace: 'normal',

  '&:before': {
    content: '""',
    position: 'absolute',
    top: 0, right: 0, bottom: 0, left: 0,

    // Prevent clicking through embeded content while editing..
    zIndex: 10,
  },

  '& > .iframely-embed': {
    marginRight: 'auto',
    marginLeft: 'auto',
  },
});


class EmbedTile extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isComplete: props.asScreenshot ? true : false,
    }

    // NOTE: Some embeds are js based, which loads async, which means
    // unknown height at start and can become taller or shorter
    // when rendered finally, which can mess up placeholder text position..
    // iframely smart iframe returns rendering priority by firing load event
    // instantly, so we can't use it so use ResizeObserver instead.
    const { onLoad = noop } = props
    this.ro = new ResizeObserver(onLoad)

    this.toggleEmbedSize = this.toggleEmbedSize.bind(this)
  }

  componentDidMount() {
    if (this.embed) {
      this.ro.observe(this.embed)
    }

    this.fetchEmbedCodeIfIncomplete()
  }

  componentWillReceiveProps() {
    this.fetchEmbedCodeIfIncomplete()
  }

  fetchEmbedCodeIfIncomplete = () => {
    if (!this.state.isComplete) {
      this.fetchEmbedCode()
    }
  }

  formatAndEncodeSrc = (src) => {
    const { smaller } = this.props.cache
    const toggle = {
      iframely: smaller ? 'less' : 'more'
    }

    const { url, query } = qs.parseUrl(src)

    // don't sort, for youtube, v=12345 needs to come first.
    const queryString = qs.stringify(
      { ...query, ...toggle },
      {sort: false}
    )

    return url + '?' + queryString
  }

  makeRequestUrl = (src, opts = {}) => {
    const {
      endpoint = IFRAMELY_API_ENDPOINT,
      asScreenshot,
    } = opts

    // TODO: add lazy load param for read-only?
    const queryParams = [
      `url=${this.formatAndEncodeSrc(src)}`,
      `api_key=${IFRAMELY_API_KEY}`,

      // for using with reactjs (https://iframely.com/docs/reactjs)
      'iframe=true',
      'omit_script=true',

      // as screenshot for preview
      asScreenshot ? 'screenshot=true' : null,
    ].filter(p => p)

    return endpoint + '?' + queryParams.join('&');
  }

  async fetchEmbedCode() {
    const {
      src,
      onLoad = noop,
      onError = noop,
      updateCache = noop,
      cache,
    } = this.props

    const url = this.makeRequestUrl(src)

    // For scoping.
    let payload, error;

    try {
      const resp = await timeoutPromiseAt(3000, fetch(url))
      payload = await resp.json()
      error = payload.error
    } catch (e) {
      error = e
    }

    if (error) {
      // TODO: Need to send to error reporting here.
      return onError(error)
    }

    updateCache({
      html: payload.html,
      smaller: cache.smaller
    })

    this.setState({ isComplete: true }, onLoad)
  }

  getEmbedFrame = () => {
    const { html } = this.props.cache
    return html
  }

  getEmbedScreenshot = () => {
    const { src, asScreenshot } = this.props
    const opts = {
      asScreenshot,
      endpoint: IFRAMELY_RELAY_ENDPOINT
    }
    const url = this.makeRequestUrl(src, opts)

    return `
      <div class="tc bg-loading">
        <img src="${url}">
      </div>
    `
  }

  getEmbedInnerHtml = () => {
    return (this.props.asScreenshot
      ? this.getEmbedScreenshot()
      : this.getEmbedFrame()
    )
  }

  toggleEmbedSize = (event) => {
    const { smaller } = this.props.cache
    const cache = { smaller: !smaller, html: null }
    const opts = { doKeepFocus: true }

    // Update isComplete to false, so that componentWillReceiveProps
    // will re-fetch embed code..
    this.setState({ isComplete: false }, () => {
      this.props.updateCache(cache, opts)
    })
  }

  render() {
    const { isSelected, attributes, cache } = this.props
    const { isComplete } = this.state
    const { smaller } = cache

    return (
      <Figure
        {...attributes}
        isSelected={isSelected}
        className="relative ma0"
      >
        <HtmlFrame
          className={cx(
            // For second loading of iframe..
            { "bg-loading": isComplete }
          )}
          innerRef={ref => this.embed = ref}
          dangerouslySetInnerHTML={{__html: this.getEmbedInnerHtml()}}
          isSelected={isSelected}
        />

        {isComplete && (
          <EmbedActionMenu>
            <LessOrMoreToggle
              smaller={smaller}
              onToggle={this.toggleEmbedSize}
            />
          </EmbedActionMenu>
        )}

        {!isComplete && (
          // display: none will still cause issue
          // for dom-to-image, so don't render at all
          <MediaLoading
            className="absolute w-100 top-0"
            isSelected={isSelected}
          />
        )}
      </Figure>
    );
  }
}

EmbedTile.defaultProps = {
  cache: {}
}


export class Embed extends React.Component {
  constructor(props) {
    super(props);

    const { data } = props.node;

    this.state = {
      src: data.get('src', null),
      cache: data.get('cache', {}),
      isReceiving: data.get('isReceiving', false),
      error: data.get('error', null),
    };

    this.handleOnError = this.handleOnError.bind(this)
    this.handleOnLoad = this.handleOnLoad.bind(this)
    this.updateCache = this.updateCache.bind(this)
    this.updateNode = this.updateNode.bind(this)
  }

  componentWillReceiveProps({ node, editor }) {
    // Sync component state with doc node state
    const src = node.data.get('src', null);
    const cache = node.data.get('cache', {});
    const isReceiving = node.data.get('isReceiving');
    const error = node.data.get('error');
    this.setState({ src, cache, isReceiving, error });

    handleOrphanReceiverNode(node, editor)
  }

  handleOnLoad = () => {
    const { editor } = this.props
    const { etc } = editor.props

    return etc.updateCaretShadowPosition()
  }

  handleOnError = (error) => {
    const nodeProps = makeNodeProps({
      type: NT.embed,
      isVoid: true,
    }, {
      // Keep src around just in case..?
      src: this.state.src,
      isReceiving: false,
      error,
    });

    this.updateNode(nodeProps)
  }

  updateCache = (cache = {}, opts = {}) => {
    cache = { ...this.state.cache, ...cache }
    const { src, isReceiving, error } = this.state

    const data = { src, isReceiving, error, cache }
    const nodeProps = makeNodeProps({
      type: NT.embed,
      isVoid: true,
    }, data)

    this.updateNode(nodeProps, opts)
  }

  updateNode = (nodeProps, opts = {}) => {
    const { editor, node } = this.props
    const change = editor.value
      .change()
      .setNodeByKey(node.key, nodeProps)

    const { doKeepFocus = false } = opts
    if (doKeepFocus) {
      change.focus()
    }

    return editor.onChange(change)
  }

  render() {
    const props = this.props
    const { isReceiving, src, cache, error } = this.state

    if (isReceiving) {
      return <EmbedReceiver {...props} />;
    }

    if (error) {
      const message = "Error, unable to embed the content."
      return <FetchErrored message={message} {...props} />;
    }

    // Shouldn't happen, but..
    if (!src) return null;

    const { attributes, isSelected, editor, renderMode } = props;
    const inPreviewMode = renderMode === RM.RENDER_FOR_PREVIEW;

    return (
      <EmbedTile
        src={src}
        cache={cache}
        isSelected={isSelected && !inPreviewMode}
        asScreenshot={inPreviewMode}
        attributes={attributes}
        onError={this.handleOnError}
        onLoad={this.handleOnLoad}
        updateCache={this.updateCache}
        editor={editor}
      />
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/slate/nodes/embed.js